// M1 throwaway: FE-owned chat lifecycle for case analytics.
//
// The per-conversation context (the page's environment) is assembled here; the
// prompt and tools come from the backend. The shared session state machine is in
// useAnalyticsChatSession.

import {
  type AnalyticsChatResult,
  useAnalyticsChatSession,
} from "src/analytics/copilot/useAnalyticsChatSession";
import { useCurrentWorkspace } from "src/api/queries";
import type { Environment } from "src/hooks/useEnvironment";

// Surface expiry on the FE a day before the backend hard limit so the user gets
// a graceful "start a new chat" message instead of a failed invoke at the edge.
const SESSION_EXPIRY_THRESHOLD_MS = 6 * 24 * 60 * 60 * 1000;
const SESSION_EXPIRED_MESSAGE =
  "This chat has expired. Start a new conversation to continue.";

// Bumped for the virtual-id cutover: an entry written by the provisioning path
// pairs a real agent id with what is now a virtual version id, which does not
// resolve. The bump drops those entries, so a tab open across the deploy starts
// a new chat instead of failing its next send.
const SESSIONS_STORAGE_KEY = "caseAnalyticsM1SessionsV2";

// Wire ids from the agents module's `COPILOT_VIRTUAL_AGENT_IDS`; no agents-table
// rows exist behind them.
const CASE_ANALYTICS_VIRTUAL_AGENT_ID = "ffffffff-0000-4000-8000-000000000003";
const CASE_ANALYTICS_VIRTUAL_VERSION_ID =
  "ffffffff-0000-4000-8000-000000000004";

// Nothing to provision: the virtual agent needs no agent or version record, so
// this resolves without a round trip and the first message goes straight to
// invoke.
const provision = (): Promise<string> =>
  Promise.resolve(CASE_ANALYTICS_VIRTUAL_AGENT_ID);

const sessionSlotKey = (
  workspaceId: string,
  dashboardId: string | undefined,
  env: Environment,
): string =>
  dashboardId
    ? `${workspaceId}:${dashboardId}:${env}`
    : `${workspaceId}:${env}`;

export const useCaseAnalyticsChat = ({
  env,
  dashboardId,
}: {
  env: Environment;
  dashboardId: string | undefined;
}): AnalyticsChatResult => {
  const { data: workspace } = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;

  return useAnalyticsChatSession({
    storageKey: SESSIONS_STORAGE_KEY,
    sessionSlotKey: workspaceId
      ? sessionSlotKey(workspaceId, dashboardId, env)
      : null,
    environment: env,
    provision,
    agentVersionId: CASE_ANALYTICS_VIRTUAL_VERSION_ID,
    sessionExpiryThresholdMs: SESSION_EXPIRY_THRESHOLD_MS,
    sessionExpiredMessage: SESSION_EXPIRED_MESSAGE,
    initialStructuredInput: { environment: env },
  });
};
